/**
 * Chutt... V2 - Mesurer Page
 * Analytics-first layout: 4 ExpandableKPI cards in 2x2 grid (like Radar)
 * No photo, no radar chart, no tabs — square cards with accordion expand
 * Luxury editorial polish: gradient chart fills, refined typography, warm palette
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Skeleton,
  Grid,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import Snackbar from '@mui/material/Snackbar';
import InsightsIcon from '@mui/icons-material/Insights';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

import ExpandableKPI from '../components/shared/ExpandableKPI';
import ReportEditor from '../components/reports/ReportEditor';
import ScrollReveal from '../components/shared/ScrollReveal';
import LastScanLabel from '../components/shared/LastScanLabel';
import GlassCard from '../components/shared/GlassCard';
import { mesurerAPI } from '../services/apiV2';
import { missionsAPI, reportsAPI, objectivesAPI } from '../services/api';
import { invalidateCache } from '../services/cache';
import { colors } from '../styles/colors';

const DATA_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 heures

// Warmer semantic colors for this page (luxury palette)
const WARM = {
  positive: '#2E9E6B',
  positiveBg: 'rgba(46,158,107,0.08)',
  negative: '#B44040',
  negativeBg: 'rgba(180,64,64,0.07)',
};

/**
 * Transform backend timeline data into flat format.
 */
function transformTimeline(rawTimeline) {
  if (!rawTimeline || !Array.isArray(rawTimeline)) return rawTimeline;

  const entries = [];

  rawTimeline.forEach(item => {
    if (item.type && item.date && item.title) {
      entries.push({
        ...item,
        action_type: item.action_type || null,
        linked_action_item_id: item.linked_action_item_id || null,
        attribution_confidence: item.attribution_confidence || null,
      });
      return;
    }

    if (item.action) {
      const action = item.action;
      entries.push({
        type: 'action',
        date: action.recorded_at || action.created_at || action.date,
        title: action.title || action.action_type || 'Action',
        description: action.description || action.notes || null,
        delta_score: null,
        delta_mentions: null,
        delta_sentiment: null,
        action_type: action.action_type || null,
        linked_action_item_id: action.linked_action_item_id || null,
        attribution_confidence: null,
      });
    }

    if (item.measurements && Array.isArray(item.measurements)) {
      item.measurements.forEach(m => {
        entries.push({
          type: 'measurement',
          date: m.measured_at || m.date,
          title: m.metric_name || 'Mesure',
          description: m.notes || null,
          delta_score: m.delta_score ?? m.value_after - m.value_before ?? null,
          delta_mentions: m.delta_mentions ?? null,
          delta_sentiment: m.delta_sentiment ?? null,
          action_type: item.action?.action_type || null,
          linked_action_item_id: item.action?.linked_action_item_id || null,
          attribution_confidence: m.attribution_confidence ?? item.latest_impact?.attribution_confidence ?? null,
        });
      });
    }

    if (item.latest_impact && (!item.measurements || item.measurements.length === 0)) {
      entries.push({
        type: 'measurement',
        date: item.latest_impact.measured_at || item.latest_impact.date,
        title: 'Impact mesure',
        description: null,
        delta_score: item.latest_impact.delta_score ?? null,
        delta_mentions: item.latest_impact.delta_mentions ?? null,
        delta_sentiment: item.latest_impact.delta_sentiment ?? null,
        action_type: item.action?.action_type || null,
        linked_action_item_id: item.action?.linked_action_item_id || null,
        attribution_confidence: item.latest_impact.attribution_confidence ?? null,
      });
    }
  });

  entries.sort((a, b) => {
    const dateA = a.date ? new Date(a.date) : new Date(0);
    const dateB = b.date ? new Date(b.date) : new Date(0);
    return dateB - dateA;
  });

  return entries;
}

const ACTION_TYPE_LABELS = {
  creer_contenu: 'Contenu',
  effacer_contenu: 'Effacer',
  seo_serp: 'SEO',
  repondre_mentions: 'Reponse',
  relations_presse: 'Relations',
  planifier_publication: 'Publication',
};

const MISSION_LABELS = {
  protect: 'Proteger',
  visibility: 'Rayonner',
  trust: 'Confiance',
  community: 'Federer',
};

const MISSION_LINE_COLORS = {
  protect: colors.primary,
  visibility: colors.info,
  trust: '#2E9E6B',
  community: colors.warning,
};

const REPORT_TYPE_CONFIG = {
  monthly: { label: 'Mensuel', color: colors.primary, bg: colors.primaryBg },
  weekly: { label: 'Hebdomadaire', color: colors.info, bg: colors.infoBg },
  daily: { label: 'Quotidien', color: colors.warning, bg: colors.warningBg },
};

/** Border-left accent colors per card domain */
const CARD_ACCENTS = {
  evolution: colors.primary,
  objectifs: colors.info,
  efficacite: '#2E9E6B',
  rapports: '#D4A843',
};

/** Reusable empty state block */
function EmptyState({ icon, title, description }) {
  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 4 }}>
      <Box sx={{
        width: 56, height: 56, borderRadius: '50%',
        bgcolor: colors.emeraldGlass,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mx: 'auto', mb: 2,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 24, color: colors.textLight } })}
      </Box>
      <Typography sx={{
        fontFamily: '"Playfair Display", Georgia, serif',
        fontSize: '1.05rem', fontWeight: 500, color: colors.charcoal, mb: 1,
      }}>
        {title}
      </Typography>
      <Typography sx={{
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '0.82rem', color: colors.textMed, lineHeight: 1.6,
        maxWidth: 360, mx: 'auto', textAlign: 'justify',
      }}>
        {description}
      </Typography>
    </Box>
  );
}

/** Custom chart legend (replaces Recharts default) */
function ChartLegend() {
  return (
    <Box sx={{ display: 'flex', gap: 2.5, mt: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
      {Object.entries(MISSION_LABELS).map(([key, label]) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 3, borderRadius: 2, bgcolor: MISSION_LINE_COLORS[key] }} />
          <Typography sx={{ fontSize: '0.72rem', fontFamily: '"DM Sans", sans-serif', color: colors.textMed, fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function MesurerPage() {
  const { selectedBrand, brands, agency, userRole, cacheVersion, lastUpdated } = useOutletContext(); // eslint-disable-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const [sendingReportId, setSendingReportId] = useState(null);
  const [mesurerSnackbar, setMesurerSnackbar] = useState(null);

  const sectionFromUrl = searchParams.get('section');
  const [openSection, setOpenSection] = useState(sectionFromUrl || 'evolution');

  // Clear query param after consuming it (avoid re-opening on re-render)
  useEffect(() => {
    if (sectionFromUrl) {
      setOpenSection(sectionFromUrl);
      setSearchParams({}, { replace: true });
    }
  }, [sectionFromUrl, setSearchParams]);

  const [timeline, setTimeline] = useState([]);
  const [, setScorecard] = useState(null); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [missions, setMissions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [, setDashboardLoading] = useState(true); // eslint-disable-line no-unused-vars

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportEditorOpen, setReportEditorOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [objectivesTab, setObjectivesTab] = useState(0);
  const [objectivesHistory, setObjectivesHistory] = useState([]);

  const brandObj = brands?.find(b => b.id === selectedBrand);
  const brandName = brandObj?.name || '';

  // Cache keys
  const dataCacheKey = `mesurer_data_${selectedBrand}`;
  const dashboardCacheKey = `mesurer_dashboard_${selectedBrand}`;

  // === Data fetching (with 24h localStorage cache) ===

  const fetchData = useCallback(async (force = false) => {
    if (!selectedBrand) return;

    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(dataCacheKey);
        if (cached) {
          const { timeline: cTimeline, scorecard: cScorecard, missions: cMissions, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setTimeline(cTimeline || []);
            setScorecard(cScorecard || null);
            setMissions(cMissions || []);
            setLoading(false);
            loadedOnce.current = true;
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }

    if (!loadedOnce.current) setLoading(true);
    setError(null);
    try {
      const [timelineRes, scorecardRes, missionsRes] = await Promise.all([
        mesurerAPI.getTimeline(selectedBrand, 20, force),
        mesurerAPI.getScorecard(selectedBrand, force),
        missionsAPI.getProgress(selectedBrand, force),
      ]);

      const timelineData = timelineRes.data?.timeline || [];
      const scorecardData = scorecardRes.data || null;
      const missionsData = (missionsRes.data || missionsRes)?.missions || [];

      setTimeline(timelineData);
      setScorecard(scorecardData);
      setMissions(missionsData);

      // Save to localStorage cache
      try {
        localStorage.setItem(dataCacheKey, JSON.stringify({
          timeline: timelineData, scorecard: scorecardData, missions: missionsData,
          timestamp: Date.now(),
        }));
      } catch (_) { /* localStorage full — ignore */ }
    } catch (err) {
      console.error('Mesurer fetch error:', err);
      setError('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
      loadedOnce.current = true;
    }
  }, [selectedBrand, dataCacheKey]);

  const fetchDashboard = useCallback(async (force = false) => {
    if (!selectedBrand) return;

    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(dashboardCacheKey);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setDashboard(data);
            setDashboardLoading(false);
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }

    setDashboardLoading(true);
    try {
      const res = await mesurerAPI.getMissionsDashboard(selectedBrand, force);
      const dashData = res.data || null;
      setDashboard(dashData);
      // Save to localStorage cache
      if (dashData) {
        try {
          localStorage.setItem(dashboardCacheKey, JSON.stringify({ data: dashData, timestamp: Date.now() }));
        } catch (_) { /* localStorage full — ignore */ }
      }
    } catch (err) {
      console.warn('Missions dashboard fetch warning:', err);
    } finally {
      setDashboardLoading(false);
    }
  }, [selectedBrand, dashboardCacheKey]);

  const fetchReports = useCallback(async () => {
    if (!selectedBrand) return;
    setReportsLoading(true);
    try {
      const res = await reportsAPI.getHistory(selectedBrand);
      const data = res.data || {};
      setReports(data.reports || data || []);
    } catch (err) {
      console.warn('Reports fetch warning:', err);
    } finally {
      setReportsLoading(false);
    }
  }, [selectedBrand]);

  useEffect(() => {
    fetchData();
    fetchDashboard();
    fetchReports();
  }, [selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when CRON freshness invalidates caches (only on actual change, not on mount)
  const prevCacheVersion = useRef(cacheVersion);
  useEffect(() => {
    if (cacheVersion !== prevCacheVersion.current) {
      prevCacheVersion.current = cacheVersion;
      fetchData();
      fetchDashboard();
    }
  }, [cacheVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (objectivesTab === 1 && selectedBrand) {
      objectivesAPI.getHistory(selectedBrand).then(res => {
        setObjectivesHistory(res.data?.objectives || []);
      }).catch(() => {});
    }
  }, [objectivesTab, selectedBrand]);

  // === Derived data ===

  const transformedTimeline = useMemo(() => transformTimeline(timeline), [timeline]);

  const topActions = useMemo(() => {
    const flat = transformTimeline(timeline);
    if (!flat) return [];
    return flat
      .filter(t => t.delta_score != null)
      .sort((a, b) => Math.abs(b.delta_score) - Math.abs(a.delta_score))
      .slice(0, 10);
  }, [timeline]);

  const totalActions = useMemo(() => {
    if (!transformedTimeline) return 0;
    return transformedTimeline.filter(t => t.type === 'action').length;
  }, [transformedTimeline]);

  const avgDelta = useMemo(() => {
    if (!topActions.length) return 0;
    const sum = topActions.reduce((acc, a) => acc + (a.delta_score || 0), 0);
    return sum / topActions.length;
  }, [topActions]);

  const bestAction = useMemo(() => {
    if (!topActions.length) return null;
    return topActions.reduce((best, a) =>
      (a.delta_score || 0) > (best.delta_score || 0) ? a : best,
      topActions[0]
    );
  }, [topActions]);

  const objectifsByMission = useMemo(() => {
    if (!missions.length) return {};
    const grouped = {};
    missions.forEach(m => {
      if (m.sub_objectives?.length) {
        grouped[m.mission_key] = {
          name: m.name,
          score: m.score,
          objectives: m.sub_objectives,
        };
      }
    });
    return grouped;
  }, [missions]);

  const objectifsTotal = useMemo(() => {
    let total = 0;
    let achieved = 0;
    Object.values(objectifsByMission).forEach(m => {
      m.objectives.forEach(o => {
        total++;
        if (o.is_achieved) achieved++;
      });
    });
    return { total, achieved };
  }, [objectifsByMission]);

  const bestMission = useMemo(() => {
    if (!missions.length) return '';
    const best = missions.reduce((b, m) => ((m.score || 0) > (b.score || 0) ? m : b), missions[0]);
    return best?.name || '';
  }, [missions]);

  const globalScore = useMemo(() => {
    if (dashboard?.score_global != null) return Math.round(dashboard.score_global);
    if (!dashboard?.current_scores) return null;
    const vals = Object.values(dashboard.current_scores);
    if (!vals.length) return null;
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }, [dashboard]);

  const evolutionSummary = useMemo(() => {
    if (!dashboard?.current_scores) return 'En attente de donnees suffisantes';
    const scores = dashboard.current_scores;
    const entries = Object.entries(scores);
    if (!entries.length) return 'En attente de donnees suffisantes';
    const best = entries.reduce((a, b) => b[1] > a[1] ? b : a);
    const avg = globalScore || 0;
    if (avg >= 70) return `Excellente reputation. ${MISSION_LABELS[best[0]] || best[0]} est votre point fort.`;
    if (avg >= 50) return `Reputation stable. Concentrez vos efforts sur les missions en retrait.`;
    return `Reputation a renforcer. Vos actions commencent a porter leurs fruits.`;
  }, [dashboard, globalScore]);

  const deltaText = useMemo(() => {
    if (!dashboard?.deltas) return null;
    const avgD = Object.values(dashboard.deltas).reduce((s, v) => s + v, 0) / Object.values(dashboard.deltas).length;
    if (Math.abs(avgD) < 0.05) return null;
    return `${avgD >= 0 ? '+' : ''}${avgD.toFixed(1)} pts par rapport au mois dernier`;
  }, [dashboard]);

  const deltaType = useMemo(() => {
    if (!dashboard?.deltas) return undefined;
    const avgD = Object.values(dashboard.deltas).reduce((s, v) => s + v, 0) / Object.values(dashboard.deltas).length;
    return avgD > 0 ? 'positive' : avgD < 0 ? 'negative' : 'neutral';
  }, [dashboard]);

  const handleToggle = (section) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      const response = await reportsAPI.downloadPdf(reportId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chutt-rapport-${brandName.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      setError('Erreur lors du telechargement du PDF');
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedBrand) return;
    try {
      await reportsAPI.generate(selectedBrand);
      invalidateCache('reports/history');
      await fetchReports();
      setSuccess('Rapport genere avec succes');
    } catch (err) {
      console.error('Report generation error:', err);
      setError('Erreur lors de la generation du rapport');
    }
  };

  const handleSendReportToClient = async (reportId) => {
    setSendingReportId(reportId);
    try {
      await reportsAPI.sendToClient(reportId);
      setMesurerSnackbar('Rapport envoye au client avec succes');
      invalidateCache('reports/history');
      await fetchReports();
    } catch (err) {
      setMesurerSnackbar(err?.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingReportId(null);
    }
  };

  if (!selectedBrand) {
    return (
      <Box sx={{ background: `linear-gradient(180deg, ${colors.ivory} 0%, ${colors.ivoryWarm} 100%)`, pb: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography
            variant="h6"
            sx={{ color: colors.textMed, fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Selectionnez une marque pour mesurer l'impact
          </Typography>
        </Box>
      </Box>
    );
  }

  const isLoading = loading;

  // === CARD WRAPPER — adds a colored left accent bar ===
  const CardWrapper = ({ accentColor, children }) => (
    <Box sx={{
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 20,
        bottom: 20,
        width: 3,
        borderRadius: '0 2px 2px 0',
        bgcolor: accentColor,
        zIndex: 2,
      },
    }}>
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        background: `linear-gradient(180deg, ${colors.ivory} 0%, ${colors.ivoryWarm} 100%)`,
        minHeight: 'calc(100vh - 80px)',
        pb: 4,
        mx: 'auto',
        px: { xs: 2, sm: 4, md: 5 },
      }}
    >
      {/* === HEADER — aligned with Radar / Comprendre / Agir === */}
      <ScrollReveal>
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 400,
                fontSize: { xs: '3.2rem', sm: '4.8rem' },
                color: colors.charcoal,
                lineHeight: 1.1,
                mb: 1,
                textWrap: 'balance',
              }}
            >
              L'impact de vos{' '}
              <Box component="em" sx={{ color: colors.primary, fontStyle: 'italic' }}>
                actions
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '1rem',
                color: colors.textMed,
                fontWeight: 400,
              }}
            >
              Impact & ROI de vos actions — {brandName}
            </Typography>
            <LastScanLabel timestamp={lastUpdated} />
        </Box>
      </ScrollReveal>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* === 2x2 GRID OF EXPANDABLE KPI CARDS === */}
      {isLoading ? (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map(i => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: '20px' }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 0 }}>
          {[
            /* ===== CARD 1: EVOLUTION ===== */
            {
              key: 'evolution',
              component: (
                <CardWrapper accentColor={CARD_ACCENTS.evolution}>
                  <ExpandableKPI
                    value={globalScore != null ? `${globalScore}` : '--'}
                    label="Score de reputation"
                    valueLabel="sur 100"
                    icon={<InsightsIcon sx={{ fontSize: 20 }} />}
                    info="Evolution de vos scores par mission (Proteger, Rayonner, Confiance, Federer) sur les 90 derniers jours."
                    summary={evolutionSummary}
                    delta={deltaText}
                    deltaType={deltaType}
                    isExpanded={openSection === 'evolution'}
                    onToggle={() => handleToggle('evolution')}
                    expandedContent={
                      <Box>
                        {dashboard?.history && dashboard.history.length > 1 ? (
                          <>
                            <Box sx={{ width: '100%', height: { xs: 280, sm: 360, md: 400 }, mb: 1 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={dashboard.history}>
                                  <defs>
                                    {Object.entries(MISSION_LINE_COLORS).map(([key, color]) => (
                                      <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={color} stopOpacity={0.12} />
                                        <stop offset="100%" stopColor={color} stopOpacity={0.01} />
                                      </linearGradient>
                                    ))}
                                  </defs>
                                  <CartesianGrid
                                    strokeDasharray="0"
                                    stroke="rgba(221,213,200,0.25)"
                                    vertical={false}
                                  />
                                  <XAxis
                                    dataKey="date"
                                    tick={{ fill: colors.textLight, fontSize: 11, fontFamily: '"DM Sans", sans-serif' }}
                                    tickLine={false}
                                    axisLine={{ stroke: 'rgba(221,213,200,0.4)', strokeWidth: 1 }}
                                    tickFormatter={(d) => {
                                      if (!d) return '';
                                      const parts = d.split('-');
                                      return `${parts[2]}/${parts[1]}`;
                                    }}
                                    dy={8}
                                  />
                                  <YAxis
                                    domain={[0, 100]}
                                    tick={{ fill: colors.textLight, fontSize: 11, fontFamily: '"DM Sans", sans-serif' }}
                                    tickCount={5}
                                    axisLine={false}
                                    tickLine={false}
                                    dx={-8}
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: '#FFFFFF',
                                      border: 'none',
                                      borderRadius: '8px',
                                      fontFamily: '"DM Sans", sans-serif',
                                      fontSize: '0.78rem',
                                      boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
                                      padding: '12px 16px',
                                    }}
                                    labelFormatter={(d) => d ? `Date: ${d}` : ''}
                                  />
                                  {Object.entries(MISSION_LABELS).map(([key, label]) => (
                                    <React.Fragment key={key}>
                                      <Area
                                        type="monotone"
                                        dataKey={key}
                                        name={label}
                                        stroke="none"
                                        fill={`url(#grad-${key})`}
                                        fillOpacity={1}
                                        dot={false}
                                        activeDot={false}
                                        legendType="none"
                                      />
                                      <Line
                                        type="monotone"
                                        dataKey={key}
                                        name={label}
                                        stroke={MISSION_LINE_COLORS[key]}
                                        strokeWidth={2.5}
                                        strokeLinecap="round"
                                        dot={false}
                                        activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: MISSION_LINE_COLORS[key] }}
                                      />
                                    </React.Fragment>
                                  ))}
                                </ComposedChart>
                              </ResponsiveContainer>
                            </Box>
                            <ChartLegend />
                          </>
                        ) : (
                          <EmptyState
                            icon={<InsightsIcon />}
                            title="L'histoire commence ici"
                            description="Votre courbe d'evolution prendra forme au fil de vos actions. Chaque effort compte."
                          />
                        )}

                        {/* Deltas chips */}
                        {dashboard?.deltas && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3, mb: 1 }}>
                            {Object.entries(dashboard.deltas).map(([key, d]) => {
                              const isUp = d > 0;
                              const isDown = d < 0;
                              return (
                                <Chip
                                  key={key}
                                  icon={isUp ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : isDown ? <TrendingDownIcon sx={{ fontSize: 16 }} /> : undefined}
                                  label={`${MISSION_LABELS[key] || key} ${d >= 0 ? '+' : ''}${d.toFixed(1)} pts`}
                                  size="small"
                                  sx={{
                                    fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.75rem',
                                    bgcolor: isUp ? WARM.positiveBg : isDown ? WARM.negativeBg : colors.neutralBg,
                                    color: isUp ? WARM.positive : isDown ? WARM.negative : colors.textMed,
                                    border: 'none',
                                    '& .MuiChip-icon': { color: isUp ? WARM.positive : isDown ? WARM.negative : colors.textMed },
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}

                        {/* AI Narrative */}
                        {dashboard?.narrative && (
                          <>
                            <Divider sx={{ my: 2.5, borderColor: 'rgba(221,213,200,0.4)' }} />
                            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.primary, mb: 1.5 }}>
                              BILAN STRATEGIQUE
                            </Typography>
                            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.92rem', color: colors.text, lineHeight: 1.7, textAlign: 'justify' }}>
                              {dashboard.narrative.replace(/[#*]/g, '').replace(/\n{3,}/g, '\n\n').trim()}
                            </Typography>
                          </>
                        )}
                      </Box>
                    }
                  />
                </CardWrapper>
              ),
            },
            /* ===== CARD 2: OBJECTIFS ===== */
            {
              key: 'objectifs',
              component: (
                <CardWrapper accentColor={CARD_ACCENTS.objectifs}>
                  <ExpandableKPI
                    value={objectifsTotal.total > 0 ? `${objectifsTotal.achieved}/${objectifsTotal.total}` : '--'}
                    label="Vos objectifs"
                    valueLabel="objectifs atteints"
                    icon={<TrackChangesIcon sx={{ fontSize: 20 }} />}
                    info="Progression de vos objectifs par mission. Chaque objectif est mesure automatiquement selon son horizon (hebdo, mensuel, trimestriel)."
                    summary={
                      objectifsTotal.total > 0
                        ? `${objectifsTotal.achieved} objectif${objectifsTotal.achieved > 1 ? 's' : ''} atteint${objectifsTotal.achieved > 1 ? 's' : ''} sur ${objectifsTotal.total}. ${bestMission ? `Mission ${bestMission} progresse le plus.` : ''}`
                        : missions.length > 0
                          ? `${missions.length} missions actives`
                          : 'En attente de configuration'
                    }
                    isExpanded={openSection === 'objectifs'}
                    onToggle={() => handleToggle('objectifs')}
                    expandedContent={
                      <Box>
                        <Tabs
                          value={objectivesTab}
                          onChange={(e, v) => setObjectivesTab(v)}
                          sx={{
                            mb: 2,
                            '& .MuiTab-root': {
                              fontFamily: '"DM Sans", sans-serif',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              textTransform: 'none',
                            },
                            '& .MuiTabs-indicator': { bgcolor: colors.primary },
                          }}
                        >
                          <Tab label="En cours" />
                          <Tab label="Historique" />
                        </Tabs>
                        {objectivesTab === 0 && (<>
                        {Object.keys(objectifsByMission).length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {Object.entries(objectifsByMission).map(([missionKey, mission], mIdx) => (
                              <Box key={missionKey}>
                                {mIdx > 0 && <Divider sx={{ my: 2.5, borderColor: 'rgba(221,213,200,0.4)' }} />}
                                <Typography variant="h6" sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: colors.charcoal, mb: 1.5 }}>
                                  {mission.name}
                                  <Chip label={`${Math.round(mission.score || 0)}/100`} size="small" sx={{ ml: 1.5, fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.68rem', bgcolor: colors.primaryBg, color: colors.primary, height: 22 }} />
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {mission.objectives.map((obj, i) => {
                                    const pct = obj.target > 0 ? Math.min(100, Math.round((obj.current / obj.target) * 100)) : 0;
                                    return (
                                      <Box key={i}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {obj.is_achieved && <CheckCircleIcon sx={{ fontSize: 16, color: WARM.positive }} />}
                                            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.text, fontWeight: obj.is_achieved ? 600 : 400 }}>
                                              {obj.name || obj.label || `Objectif ${i + 1}`}
                                            </Typography>
                                            {obj.horizon && (
                                              <Chip label={obj.horizon} size="small" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.65rem', height: 20, bgcolor: colors.neutralBg, color: colors.textMed }} />
                                            )}
                                          </Box>
                                          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: obj.is_achieved ? WARM.positive : colors.textMed, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                            {obj.current ?? 0} / {obj.target ?? 0} {obj.unit || ''} ({pct}%)
                                          </Typography>
                                        </Box>
                                        <LinearProgress
                                          variant="determinate"
                                          value={pct}
                                          sx={{
                                            height: 5,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(221,213,200,0.2)',
                                            '& .MuiLinearProgress-bar': {
                                              borderRadius: 3,
                                              bgcolor: obj.is_achieved ? WARM.positive : colors.primary,
                                              transition: 'transform 0.8s cubic-bezier(0.4,0,0.2,1)',
                                            },
                                          }}
                                        />
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <EmptyState
                            icon={<TrackChangesIcon />}
                            title="Definissez vos ambitions"
                            description="Configurez vos objectifs dans les parametres. Nous mesurerons votre progression automatiquement."
                          />
                        )}
                        </>)}
                        {objectivesTab === 1 && (
                          <Box>
                            {objectivesHistory.length === 0 ? (
                              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textLight, textAlign: 'center', py: 3 }}>
                                Aucun objectif cloture
                              </Typography>
                            ) : objectivesHistory.map((obj, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${colors.borderLight}` }}>
                                <Box>
                                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', fontWeight: 500, color: colors.charcoal }}>
                                    {obj.type_info?.name || obj.objective_type}
                                  </Typography>
                                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textMed }}>
                                    {obj.final_value != null ? `Valeur finale: ${Math.round(obj.final_value)}` : ''} | Cible: {Math.round(obj.target_value || 0)}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={
                                    { achieved: 'Atteint', failed: 'Echoue', abandoned: 'Abandonne' }[obj.status] || obj.status
                                  }
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.68rem',
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontWeight: 600,
                                    bgcolor: {
                                      achieved: 'rgba(34,197,94,0.1)',
                                      failed: 'rgba(239,68,68,0.1)',
                                      abandoned: colors.gray100,
                                    }[obj.status] || colors.gray100,
                                    color: {
                                      achieved: colors.success,
                                      failed: colors.danger,
                                      abandoned: colors.textLight,
                                    }[obj.status] || colors.textMed,
                                  }}
                                />
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </CardWrapper>
              ),
            },
            /* ===== CARD 3: EFFICACITE ===== */
            {
              key: 'efficacite',
              component: (
                <CardWrapper accentColor={CARD_ACCENTS.efficacite}>
                  <ExpandableKPI
                    value={totalActions || 0}
                    label="Impact de vos actions"
                    valueLabel="actions mesurees"
                    icon={<ShowChartIcon sx={{ fontSize: 20 }} />}
                    info="Classement de vos actions par impact mesure (delta score). Identifie les actions les plus efficaces."
                    summary={
                      totalActions > 0
                        ? `${totalActions} action${totalActions > 1 ? 's' : ''} realisee${totalActions > 1 ? 's' : ''} avec un impact moyen de ${avgDelta >= 0 ? '+' : ''}${avgDelta.toFixed(1)} pts.`
                        : 'En attente de mesures'
                    }
                    delta={
                      bestAction
                        ? `Meilleure : ${bestAction.title?.slice(0, 30)}${(bestAction.title?.length || 0) > 30 ? '...' : ''}`
                        : undefined
                    }
                    deltaType={bestAction ? 'positive' : undefined}
                    isExpanded={openSection === 'efficacite'}
                    onToggle={() => handleToggle('efficacite')}
                    expandedContent={
                      <Box>
                        {/* 3 mini metrics */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                          <GlassCard hover={false} glow={false} sx={{ borderRadius: '14px' }}>
                            <Box sx={{ p: '20px 22px' }}>
                              <Typography sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textLight, fontWeight: 600, fontFamily: '"DM Sans", sans-serif', mb: 0.5 }}>
                                TOTAL ACTIONS
                              </Typography>
                              <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.8rem', fontWeight: 600, color: colors.charcoal, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                                {totalActions}
                              </Typography>
                            </Box>
                          </GlassCard>
                          <GlassCard hover={false} glow={false} sx={{ borderRadius: '14px' }}>
                            <Box sx={{ p: '20px 22px' }}>
                              <Typography sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textLight, fontWeight: 600, fontFamily: '"DM Sans", sans-serif', mb: 0.5 }}>
                                DELTA SCORE MOYEN
                              </Typography>
                              <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.8rem', fontWeight: 600, color: avgDelta >= 0 ? WARM.positive : WARM.negative, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                                {avgDelta >= 0 ? '+' : ''}{avgDelta.toFixed(1)}
                              </Typography>
                            </Box>
                          </GlassCard>
                          <GlassCard hover={false} glow={false} sx={{ borderRadius: '14px' }}>
                            <Box sx={{ p: '20px 22px' }}>
                              <Typography sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textLight, fontWeight: 600, fontFamily: '"DM Sans", sans-serif', mb: 0.5 }}>
                                MEILLEURE ACTION
                              </Typography>
                              <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.1rem', fontWeight: 600, color: colors.charcoal, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {bestAction?.title || '--'}
                              </Typography>
                              {bestAction?.delta_score != null && (
                                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: WARM.positive, fontWeight: 600 }}>
                                  +{bestAction.delta_score.toFixed(1)} pts
                                </Typography>
                              )}
                            </Box>
                          </GlassCard>
                        </Box>

                        {/* Top 10 actions */}
                        {topActions.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textLight, mb: 1 }}>
                              TOP ACTIONS PAR IMPACT
                            </Typography>
                            {topActions.map((action, i) => {
                              const isBest = bestAction && action.title === bestAction.title && action.date === bestAction.date;
                              const isWorst = i === topActions.length - 1 && (action.delta_score || 0) < 0;
                              const d = action.delta_score || 0;
                              return (
                                <Box key={i} sx={{
                                  display: 'flex', alignItems: 'center', gap: 2,
                                  p: '10px 16px', borderRadius: '14px',
                                  bgcolor: isBest ? WARM.positiveBg : isWorst ? WARM.negativeBg : 'transparent',
                                  '&:hover': { bgcolor: 'rgba(221,213,200,0.15)' },
                                  transition: 'background-color 0.2s ease',
                                }}>
                                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '0.85rem', fontWeight: 500, color: colors.textLight, minWidth: 24, fontVariantNumeric: 'tabular-nums' }}>
                                    {i + 1}.
                                  </Typography>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', fontWeight: 500, color: colors.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {action.title}
                                    </Typography>
                                  </Box>
                                  {action.action_type && ACTION_TYPE_LABELS[action.action_type] && (
                                    <Chip label={ACTION_TYPE_LABELS[action.action_type]} size="small" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.68rem', fontWeight: 600, height: 22, bgcolor: colors.primaryBg, color: colors.primary, flexShrink: 0 }} />
                                  )}
                                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: colors.textLight, flexShrink: 0, minWidth: 60, textAlign: 'right' }}>
                                    {action.date ? new Date(action.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}
                                  </Typography>
                                  <Chip label={`${d >= 0 ? '+' : ''}${d.toFixed(1)}`} size="small" sx={{
                                    fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', fontWeight: 700, height: 24, minWidth: 56,
                                    bgcolor: d >= 0 ? WARM.positiveBg : WARM.negativeBg,
                                    color: d >= 0 ? WARM.positive : WARM.negative,
                                    flexShrink: 0,
                                  }} />
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <EmptyState
                            icon={<ShowChartIcon />}
                            title="L'impact se mesure dans le temps"
                            description="Les resultats de vos actions apparaitront ici une fois les premieres mesures collectees."
                          />
                        )}
                      </Box>
                    }
                  />
                </CardWrapper>
              ),
            },
            /* ===== CARD 4: RAPPORTS ===== */
            {
              key: 'rapports',
              component: (
                <CardWrapper accentColor={CARD_ACCENTS.rapports}>
                  <ExpandableKPI
                    value={reports.length || 0}
                    label="Vos rapports"
                    valueLabel="rapports generes"
                    icon={<ArticleOutlinedIcon sx={{ fontSize: 20 }} />}
                    info="Historique de vos rapports de reputation. Generez, editez et telechargez vos rapports PDF."
                    summary={
                      reports.length > 0
                        ? `Dernier rapport genere le ${
                            (() => {
                              const d = reports[0]?.report_date || reports[0]?.date || reports[0]?.generated_at;
                              return d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
                            })()
                          }.`
                        : 'Aucun rapport genere pour le moment'
                    }
                    delta={reports.length > 0 ? `${reports.length} disponible${reports.length > 1 ? 's' : ''}` : undefined}
                    deltaType="neutral"
                    isExpanded={openSection === 'rapports'}
                    onToggle={() => handleToggle('rapports')}
                    expandedContent={
                      <Box>
                        {/* Generate button + description */}
                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Button
                            variant="contained"
                            onClick={handleGenerateReport}
                            sx={{ bgcolor: colors.primary, borderRadius: '100px', textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, px: 3, '&:hover': { bgcolor: colors.primaryDark } }}
                          >
                            Generer un rapport
                          </Button>
                          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textLight, fontStyle: 'italic' }}>
                            PDF editable avant envoi
                          </Typography>
                        </Box>

                        {/* Reports list */}
                        {reportsLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} sx={{ color: colors.primary }} />
                          </Box>
                        ) : reports.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {reports.map((report, i) => {
                              const reportDate = report.report_date || report.date || report.generated_at;
                              const reportType = report.report_type || report.type || 'monthly';
                              const typeConfig = REPORT_TYPE_CONFIG[reportType] || REPORT_TYPE_CONFIG.monthly;
                              return (
                                <Box key={report.id || i} sx={{
                                  display: 'flex', alignItems: 'center', gap: 2,
                                  p: '12px 16px', borderRadius: '14px',
                                  border: '1px solid transparent',
                                  '&:hover': { bgcolor: colors.emeraldGlass, borderColor: 'rgba(221,213,200,0.4)' },
                                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                }}>
                                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '0.95rem', fontWeight: 500, color: colors.charcoal, minWidth: 100 }}>
                                    {reportDate ? new Date(reportDate).toLocaleDateString('fr-FR') : ''}
                                  </Typography>
                                  <Chip label={typeConfig.label} size="small" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.68rem', fontWeight: 600, height: 22, bgcolor: typeConfig.bg, color: typeConfig.color }} />
                                  {report.nss != null && (
                                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: report.nss >= 0 ? WARM.positive : WARM.negative, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                      NSS: {report.nss >= 0 ? '+' : ''}{typeof report.nss === 'number' ? report.nss.toFixed(0) : report.nss}
                                    </Typography>
                                  )}
                                  <Box sx={{ flex: 1 }} />
                                  <IconButton size="small" aria-label="Telecharger le rapport PDF" onClick={() => handleDownloadPdf(report.report_id)} title="Telecharger PDF"
                                    sx={{ color: colors.textLight, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { color: colors.primary, bgcolor: colors.primaryBg } }}>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" aria-label="Editer le rapport" onClick={() => { setSelectedReport(report); setReportEditorOpen(true); }} title="Editer"
                                    sx={{ color: colors.textLight, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { color: colors.primary, bgcolor: colors.primaryBg } }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  {userRole === 'admin_agence' && report.review_status === 'pending_review' && (
                                    <IconButton
                                      size="small"
                                      aria-label="Envoyer au client"
                                      title="Envoyer au client"
                                      disabled={sendingReportId === report.report_id}
                                      onClick={() => handleSendReportToClient(report.report_id)}
                                      sx={{ color: colors.primary, '&:hover': { color: colors.white, bgcolor: colors.primary } }}
                                    >
                                      {sendingReportId === report.report_id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                                    </IconButton>
                                  )}
                                  {report.review_status === 'sent' && (
                                    <Chip label="Envoye" size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 600, fontFamily: '"DM Sans", sans-serif', borderRadius: '100px', bgcolor: colors.successBg, color: colors.success }} />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <EmptyState
                            icon={<ArticleOutlinedIcon />}
                            title="Votre premier rapport vous attend"
                            description="Generez un rapport PDF personnalise pour synthetiser votre reputation et le partager."
                          />
                        )}
                      </Box>
                    }
                  />
                </CardWrapper>
              ),
            },
          ].map((kpi) => (
            <Grid
              item
              key={kpi.key}
              xs={12}
              sm={openSection === kpi.key ? 12 : 6}
            >
              {kpi.component}
            </Grid>
          ))}
        </Grid>
      )}

      {/* === REPORT EDITOR DIALOG === */}
      <ReportEditor
        open={reportEditorOpen}
        onClose={() => {
          setReportEditorOpen(false);
          setSelectedReport(null);
        }}
        brandId={selectedBrand}
        brandName={brandName}
        agencyLogoUrl={agency?.logo_url}
        reportId={selectedReport?.report_id}
      />

      <Snackbar
        open={!!mesurerSnackbar}
        autoHideDuration={4000}
        onClose={() => setMesurerSnackbar(null)}
        message={mesurerSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default MesurerPage;
